/**
 * Frontend port definitions.
 *
 * Page components depend on these interfaces, never on a vendor SDK. The
 * Firebase client SDK is imported in exactly two files
 * (`firebase/auth-provider.ts` and `firebase/storage-provider.ts`), so
 * swapping providers for a China deployment means adding two files and one
 * registry entry rather than editing every screen.
 */

export type AuthErrorCode =
  | "invalid_credentials"
  | "email_in_use"
  | "weak_password"
  | "invalid_email"
  | "too_many_requests"
  | "user_disabled"
  | "requires_recent_login"
  | "network"
  | "unknown";

/**
 * A vendor-neutral authentication failure.
 *
 * `code` maps onto a key in the message catalogue, so the same failure is
 * reported correctly in both languages without the UI inspecting vendor
 * error strings.
 */
export class AuthError extends Error {
  readonly code: AuthErrorCode;

  constructor(code: AuthErrorCode, message?: string) {
    super(message ?? code);
    this.name = "AuthError";
    this.code = code;
  }
}

/** The signed-in user as the UI needs to know them. */
export interface AuthSession {
  readonly providerUserId: string;
  readonly email: string;
  readonly emailVerified: boolean;
  readonly displayName: string | null;
  /** Role as claimed by the token. The server re-checks it on every call. */
  readonly claimedRole: "user" | "admin" | null;
}

export interface AuthProviderPort {
  readonly providerName: string;
  readonly configured: boolean;

  /** Fires immediately with the current state, then on every change. */
  subscribe(listener: (session: AuthSession | null) => void): () => void;

  /**
   * Returns a bearer token for API calls.
   *
   * `forceRefresh` is used after a role change so the new claim is picked up
   * without making the user sign in again.
   */
  getAccessToken(forceRefresh?: boolean): Promise<string | null>;

  registerWithEmail(input: {
    email: string;
    password: string;
    displayName: string;
    locale: string;
  }): Promise<AuthSession>;

  signInWithEmail(email: string, password: string): Promise<AuthSession>;

  signOut(): Promise<void>;

  sendVerificationEmail(locale: string, continueUrl: string): Promise<void>;

  sendPasswordReset(
    email: string,
    locale: string,
    continueUrl: string,
  ): Promise<void>;

  /** Re-reads the account so a just-completed verification is noticed. */
  refreshSession(): Promise<AuthSession | null>;
}

export interface UploadHandle {
  /** Resolves when the bytes are stored; rejects on failure or cancellation. */
  readonly completion: Promise<void>;
  cancel(): void;
  pause(): void;
  resume(): void;
}

export interface UploadRequest {
  readonly bucket: string;
  readonly objectPath: string;
  readonly contentType: string;
  readonly data: Blob;
  readonly onProgress: (transferredBytes: number, totalBytes: number) => void;
}

export class UploadCancelledError extends Error {
  constructor() {
    super("upload_cancelled");
    this.name = "UploadCancelledError";
  }
}

/**
 * Direct browser-to-storage upload.
 *
 * The implementation must be resumable where the platform supports it, and
 * must surface progress and cancellation -- a three-minute video over a
 * phone connection is not something to run blind.
 */
export interface StorageUploaderPort {
  readonly providerName: string;
  readonly configured: boolean;
  upload(request: UploadRequest): UploadHandle;
}

/** Thrown by region placeholders so the UI can render an honest empty state. */
export class ProviderNotConfiguredError extends Error {
  readonly provider: string;

  constructor(provider: string) {
    super(
      `Provider "${provider}" is not configured for this deployment region.`,
    );
    this.name = "ProviderNotConfiguredError";
    this.provider = provider;
  }
}
